import React, { useMemo } from "react";
import { CrossTabCell, CrossTabResult, RowSeverity } from "./pivotUtils";
import { formatAbbreviatedMoney } from "../Money";
import { Tooltip } from "primereact/tooltip";
import styles from "./PivotTable.module.css";

type CrossTabTableProps = {
  data: CrossTabResult;
  rowLabel: string;
  colLabel: string;
  tableRef: React.Ref<HTMLTableElement>;
};

const MAX_COLUMNS = 15;

const severityClassName = (severity: RowSeverity | null | undefined) => {
  if (severity === "tier_1") return styles.severityTier1Cell;
  if (severity === "tier_2") return styles.severityTier2Cell;
  if (severity === "invalid") return styles.severityInvalidCell;
  return "";
};

const cx = (...names: (string | false | null | undefined)[]) =>
  names.filter(Boolean).join(" ");

const CELL_TOOLTIP_CLASS = "ct-cell-tip";

const cellContent = (cell: CrossTabCell | undefined) => {
  if (!cell || cell.tiv === 0) return null;
  return (
    <span
      className={CELL_TOOLTIP_CLASS}
      data-pr-tooltip={`${cell.count.toLocaleString()} locations`}
    >
      {formatAbbreviatedMoney(cell.tiv)}
    </span>
  );
};

export const CrossTabTable: React.FC<CrossTabTableProps> = ({
  data,
  rowLabel,
  colLabel,
  tableRef,
}) => {
  const visibleCols = useMemo(
    () =>
      data.columnKeys.length > MAX_COLUMNS
        ? data.columnKeys.slice(0, MAX_COLUMNS)
        : data.columnKeys,
    [data.columnKeys]
  );
  const truncated = data.columnKeys.length > MAX_COLUMNS;

  return (
    <div className={styles.tableScroller}>
      <Tooltip target={`.${CELL_TOOLTIP_CLASS}`} position="top" />
      {truncated && (
        <div className={styles.crossTabTruncation}>
          Showing top {MAX_COLUMNS} of {data.columnKeys.length} {colLabel}{" "}
          values by TIV.
        </div>
      )}
      <table className={cx(styles.table, styles.crossTab)} ref={tableRef}>
        <thead>
          <tr>
            <th className={cx(styles.thLabel, styles.stickyCol)}>
              {rowLabel}
              <span className={styles.headerSub}>› {colLabel}</span>
            </th>
            {visibleCols.map((ck) => (
              <th key={ck} className={cx(styles.thNum, styles.crossTabCol)}>
                {ck}
              </th>
            ))}
            <th className={cx(styles.thNum, styles.crossTabTotal)}>Total</th>
            <th className={styles.filler} />
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row) => (
            <tr key={row.rowKey} className={styles.row}>
              <td className={cx(styles.tdLabel, styles.stickyCol)}>
                {row.rowKey}
              </td>
              {visibleCols.map((ck) => {
                const cell = row.cells[ck];
                return (
                  <td
                    key={ck}
                    className={cx(
                      styles.tdNum,
                      severityClassName(cell?.severity)
                    )}
                  >
                    {cellContent(cell)}
                  </td>
                );
              })}
              <td className={cx(styles.tdNum, styles.crossTabTotal)}>
                {row.total.tiv !== 0
                  ? formatAbbreviatedMoney(row.total.tiv)
                  : null}
              </td>
              <td className={styles.filler} />
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td className={cx(styles.tdLabel, styles.stickyCol)}>Total</td>
            {visibleCols.map((ck) => {
              const ct = data.columnTotals.get(ck);
              return (
                <td key={ck} className={styles.tdNum}>
                  {ct ? formatAbbreviatedMoney(ct.tiv) : ""}
                </td>
              );
            })}
            <td className={cx(styles.tdNum, styles.crossTabTotal)}>
              {formatAbbreviatedMoney(data.grandTotal.tiv)}
            </td>
            <td className={styles.filler} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};
